// Learn tab progress rules (issue #3470): levels and their tunables, session scoring, unlocks, best
// scores, streak arithmetic, voice part from range and clef from part. Pure functions over the
// LearnProgress record in model.ts, shared by the client and the server. No DOM, no audio.

import {
  addDayOffset,
  dateCompare,
  DateString,
  dateStringFromDate,
  DayOfWeek,
  getDayOfWeek,
  mostRecentSunday,
} from './date_string.ts';
import {
  LearnLevel,
  LearnModule,
  LearnModuleProgress,
  LearnPracticeDay,
  LearnProgress,
  VocalRange,
  VoicePart,
} from './model.ts';

// ---- Levels and modules ----------------------------------------------------------------------

export const learnLevels: LearnLevel[] = [LearnLevel.Easy, LearnLevel.Medium, LearnLevel.Hard]; // in unlock order

const learnLevelNames: Record<LearnLevel, string> = {
  [LearnLevel.Easy]: 'Easy',
  [LearnLevel.Medium]: 'Medium',
  [LearnLevel.Hard]: 'Hard',
};

export function learnLevelName(level: LearnLevel): string {
  return learnLevelNames[level];
}

const learnModuleNames: Record<LearnModule, string> = {
  [LearnModule.PitchMatch]: 'Pitch Match',
  [LearnModule.RhythmEcho]: 'Rhythm Echo',
  [LearnModule.IntervalTraining]: 'Interval Training',
  [LearnModule.SingInSolfege]: 'Sing in Solfège',
  [LearnModule.SingRhythm]: 'Sing Rhythm',
};

export function learnModuleName(module: LearnModule): string {
  return learnModuleNames[module];
}

export function nextLearnLevel(level: LearnLevel): LearnLevel | undefined {
  return learnLevels[learnLevels.indexOf(level) + 1];
}

// ---- Voice parts and ranges ------------------------------------------------------------------

export const defaultVocalRange: VocalRange = { lowMidi: 55, highMidi: 74 }; // G3 to D5, until the Range Finder runs
export const rangeTrimSemitones = 1; // exercises stay inside the measured range, trimmed at each end

// Every singer is assigned one of the four parts; an in-between range (baritone) takes the nearer
// midpoint. Assumed comfortable ranges: soprano C4-A5, alto G3-E5, tenor C3-A4, bass E2-E4.
const voicePartMidpoints: Array<{ part: VoicePart; midi: number }> = [
  { part: VoicePart.Soprano, midi: 70.5 },
  { part: VoicePart.Alto, midi: 65.5 },
  { part: VoicePart.Tenor, midi: 58.5 },
  { part: VoicePart.Bass, midi: 52 },
];

export function voicePartForRange(range: VocalRange): VoicePart {
  const midpoint = (range.lowMidi + range.highMidi) / 2;
  let best = voicePartMidpoints[0];
  for (const candidate of voicePartMidpoints) {
    if (Math.abs(candidate.midi - midpoint) < Math.abs(best.midi - midpoint)) {
      best = candidate;
    }
  }
  return best.part;
}

export function voicePartName(part: VoicePart): string {
  return part.charAt(0).toUpperCase() + part.slice(1);
}

// "You are a tenor" / "You are an alto", for the hub subheading under the range
export function voicePartSentence(part: VoicePart): string {
  const article = /^[aeiou]/.test(part) ? 'an' : 'a';
  return `You are ${article} ${part}`;
}

export type Clef = 'treble' | 'bass';

export function clefForVoicePart(part: VoicePart | undefined): Clef {
  return part === VoicePart.Tenor || part === VoicePart.Bass ? 'bass' : 'treble';
}

// Flat spellings match the note names used by the piano soundfont (client/src/player/instrument.ts)
const flatNoteNames = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];

export function midiToNoteName(midi: number): string {
  const rounded = Math.round(midi);
  const octave = Math.floor(rounded / 12) - 1;
  return flatNoteNames[((rounded % 12) + 12) % 12] + octave.toString();
}

// "G3 to D5"
export function formatVocalRange(range: VocalRange): string {
  return `${midiToNoteName(range.lowMidi)} to ${midiToNoteName(range.highMidi)}`;
}

// ---- Pitch Match tunables (issue #3338 starting values) --------------------------------------

export interface PitchLevelParams {
  toleranceCents: number;
  holdSeconds: number;
  octaveCounts: boolean; // a note sung an octave off counts as correct
  maxJumpSemitones: number; // distance from the previous target to the next one
}

export const pitchLevelParams: Record<LearnLevel, PitchLevelParams> = {
  [LearnLevel.Easy]: { toleranceCents: 50, holdSeconds: 1.0, octaveCounts: true, maxJumpSemitones: 5 },
  [LearnLevel.Medium]: { toleranceCents: 35, holdSeconds: 1.5, octaveCounts: true, maxJumpSemitones: 12 },
  [LearnLevel.Hard]: { toleranceCents: 20, holdSeconds: 2.0, octaveCounts: false, maxJumpSemitones: 12 },
};

export const pitchNotesPerSession = 10;

// What every Learn exercise that listens to a voice believes about the detector's frames and about
// the piano guide it listens after, so the Range Finder and Pitch Match hear the same thing: both
// sets of tunables spread these in (issue #3518) and the phone validation pass tunes them here
// once. The two exercises call the same value different things on screen - the Range Finder's
// "step" is Pitch Match's "note" - but the numbers are one set. Not every number is read by both:
// the hysteresis floor, the held voice's floor and clarity, the presence gate's margin, the two
// clarities below the voiced bar, the three bands that weigh the detectors against each other, the
// four limits that weigh a reading against the voice before it and the smoothing are Pitch Match's
// alone so far (the Range Finder asks only the entry questions), and they live here all the same,
// because a device pass that moves the voiced bar has to move what hangs off it in the same breath.
export const learnListeningTunables = {
  matchGapMs: 150, // a dropout shorter than this does not reset a hold
  voicedDbfs: -50, // quieter frames are silence (audio-research.md)
  // Once a voice has been heard it stays heard down to this level (frame_reading's isSilent, asked
  // with `heard`); entry is still voicedDbfs. Pitch Match alone asks that form, and it pays for it
  // with a wider window in which an echo canceller's leak of the piano still reads as the singer.
  unvoicedDbfs: -56,
  // A held voice under the silence bar (note_scorer.ts): once a reading exists, a frame below the
  // bar still counts when it is at least this loud, the target estimator reads it at
  // heldVoiceClarity or better and within jumpCents of the reading kept. On the Galaxy A16 the
  // phone's own processing turned a held note down about 30 dB within 100 to 150 ms, from about
  // -24 to -60 dBFS, and left it at -57 to -67 dBFS for up to 2 s while the estimator read the
  // singer at clarity 0.95 to 1.00 (issue #3708, 2026-10-04). It is also the analysis worker's own
  // floor (defaultDetectorConfig.silenceDbfs): nothing quieter is read at all.
  heldVoiceDbfs: -70,
  // The estimator's clarity a frame under the silence bar needs to be the held voice. In the A16
  // captures 0.8 or 0.9 lost part of one held note, and with no clarity bar the estimator's
  // octave-only reading of noise at -68 dBFS got through (issue #3708, 2026-10-04).
  heldVoiceClarity: 0.7,
  // The presence gate (note_scorer.ts): a frame the app's own echo canceller subtracted an echo
  // from is the guide, not the singer, when what it left lies more than this many dB under the echo
  // it took out. Tuned offline on the four Samsung double-talk fixtures (issue #3708, 2026-10-04):
  // on the Galaxy A16 the guide alone leaves 16 to 27 dB under the echo, and 15 takes every such
  // frame out (16 lets 1 percent through, 17 lets 16 percent through), while a singer on the
  // guide's own note sits mostly 0 to 14 dB under it, so each dB lower costs that singer frames
  // (14 kept 89 percent of the soft singer's counted frames, 15 keeps 91). The guide alone on the
  // Galaxy A03s sits as near as a singer does, and no margin parts them there.
  echoMarginDb: 15,
  voicedClarity: 0.7, // detector confidence needed to trust a pitch (research: 0.7 for the target estimator)
  // The target estimator's reading draws the dot and the hint from this clarity; counting toward
  // the hold still needs voicedClarity, and a reading between the two is flagged weak: it draws,
  // says "A little louder", and feeds the too-noisy count like any other frame with no note in it
  dotClarity: 0.5,
  octaveMargin: 0.15, // octaveBelow/octaveAbove must beat the main correlation by this to mean an octave error
  // The known-target estimator searches for the singer's period only this far either side of the
  // target's (target.ts's defaultSearchCents, which a test holds this to), so a singer further off
  // is out of its sight - and it does not fall quiet then: a low hum's harmonics line up inside the
  // band and hand it a false peak. When it and YIN are both confident and read further apart than
  // this, other than at a harmonic ratio, Pitch Match takes the estimator's peak for a false one
  // and reads the frame by YIN (issue #3587, the phone captures).
  estimatorBandCents: 150,
  // How close the gap between two readings of one voice must come to a harmonic ratio (2:1, 3:2,
  // 1:2 and the rest) to be read as a detector on an overtone or a subharmonic rather than as two
  // different notes: a semitone either side. In the phone captures the confident detectors'
  // harmonic disagreements sit within 35 cents of the exact ratio and the false peaks' at least
  // 180 cents from every ratio, so this is room on both sides rather than a fine line (issue #3587).
  harmonicWindowCents: 100,
  // How far from an exact octave off the target a YIN reading may be and still be that octave, on
  // the levels where octaves count. The level's tolerance used to decide it, and a phone capture
  // shows why that is too tight: a singer an octave below and 110 cents sharp, whom the estimator
  // folded (+111, "an octave low is fine") while YIN read -1040 and did not, so the dot bounced a
  // full octave between them. A reading this close to an octave is not a different note; folding it
  // gives the advice the estimator gives for the same voice. The window is not free: YIN's 2:3 error
  // on a strong third harmonic reads a voice 702 cents low, which lands inside it for a singer 300
  // to 700 cents flat, and at a phrase's opening, with no reading before it to weigh it against, it
  // shows as an octave below on the wrong side of the target. So it is as narrow as the captures
  // allow: 200 cents takes capture 3's YIN readings, 117 to 161 cents off the exact octave, with
  // room to spare, and over the first major third of flat singing, where a singer finding the note
  // mostly is, it halves the stretch that misreads (200 to 400 cents flat at 300, now 300 to 400),
  // capture 1's opening at 247 and 274 cents off the octave among it (issue #3587).
  octaveFoldCents: 200,
  // How far a frame's reading may land from the reading the frame before it accepted and still be
  // the same voice moving: no voice moves 300 cents in the 11 ms between two frames, so a reading
  // further off is a detector changing its mind, and Pitch Match weighs it against the voice before
  // it shows it (continuity, in note_scorer.ts). It is also how close two frames of such a
  // jump must read to agree with each other (issue #3587, the phone captures).
  jumpCents: 300,
  // How long a reading at a harmonic of the voice is folded back onto the voice before it is
  // believed. The phone captures show YIN on the voice's harmonics and an octave below it for 50 to
  // 140 ms at a stretch, the longest the octave below, 14 frames 139 ms from first to last; at
  // 150 ms a run 11 ms longer would have been taken for a leap, so this clears it by more than
  // 100 ms. The cost is a singer who really leaps to a harmonic of where they were, which the octave
  // rule has not already folded onto the target: they are shown a quarter of a second after the
  // leap and followed from there at the rate cap, still a fraction of a second, so the fold cannot
  // hold them on the wrong octave (issue #3587).
  leapPersistMs: 250,
  // The fastest an accepted reading may move, in cents per ms: an octave in 120 ms. Glides, hunting
  // and vibrato move under 2 cents per ms and never meet it, a fast legato leap is followed within
  // a tenth of a second, and a glitch the other rules let through becomes a twitch rather than a
  // leap. Deliberately loose, for a song mode one day, which may want it tuned (issue #3587).
  maxCentsPerMs: 10,
  // How long the frames must have read the singer without a break, each reading within jumpCents of
  // the one before, before continuity may weigh a reading against them (the three limits above); a
  // reading that jumps further starts the count again from itself. A phrase's opening is where YIN
  // errs most and where there is no reading before it to catch the error, and continuity that took
  // the first reading, or a glitch among the first, for the voice would hold the right one back as
  // a jump from it: capture 1 opens a phrase with YIN at 2:3 of a voice 200 cents flat, and with
  // the track in charge from its first frame the dot was walked down from the wrong side of the
  // target at the rate cap and the words said "Hold it!" for 85 ms, where the wrong reading alone
  // had lasted 20 ms. On a phone (94 frames a second) a run's first three readings are shown as
  // read and its fourth, 32 ms in, is the first weighed. In capture 1 the wrong readings last
  // exactly three frames, so there the narrower octave fold is what mends the opening, and
  // continuity still folds two frames of the voice onto the 2:3 reading before a dropout ends the
  // run, while the words say "A little higher" throughout; a device pass that finds openings wrong
  // for longer can raise this (issue #3587).
  trackMinMs: 30,
  // The time constant of the exponential smoothing the note scorer applies to every scored
  // reading's cents: a step in the voice is 63% shown after this long and 95% after three times it.
  // Close to the feel the design chose: the dot's CSS transition, restarted by every frame, behaved
  // on Chromium as a smoother with a time constant near this (about 75 ms behind a moving voice),
  // now applied once to the reading itself so the words, the dot and the hold share it (issue #3587).
  // It sits with the other listening tunables so the device validation pass tunes them together.
  readingSmoothingMs: 60,
  frameDeltaCapMs: 250, // a gap between frames counts as at most this (a backgrounded tab)
  guideMs: 1000, // how long the piano guide sounds
  guideTailMs: 250, // play-then-listen: frames still ignored after the guide (release, canceller settling)
  silenceReplayMs: 5000, // silence in one note or step before the guide replays with the nudge
  tooNoisyAfterMs: 6000, // sound without a confident pitch before the too-noisy screen
};

// Pitch Match's own timings, in ms on the audio clock (issue #3518); the guide, silence and noise
// timings it shares with the Range Finder are in learnListeningTunables above.
export const pitchMatchTunables = {
  // Sing-along: the piano's own ring. The guide note is scheduled for this long and fades at the
  // end of it; it is not re-struck, so the singer hears the note once, it keeps sounding while
  // they find it, and it comes back only when they ask for it with "Play again" (issue #3518).
  // Measured from the soundfont's samples: C3 8.0 s, C4 8.2 s, G4 8.4 s, A5 6.8 s.
  singAlongGuideMs: 8000,
  lockPauseMs: 1000, // from a lock to the next note
  missPauseMs: 1000, // from a miss to the next note
  // Listening this long on one guide with no lock misses the note, so a note an echo canceller
  // keeps "voiced" - the piano's own sound coming back in - still ends, rather than holding the
  // session open forever.
  noteTimeoutMs: 20000,
};

// ---- Interval Training tunables (issue #3721 starting values) --------------------------------

// What each sung note of a pattern is scored against; the note scorer reads these three as it reads
// Pitch Match's (note_scorer.ts). The hold is shorter than Pitch Match's because a pattern is
// several notes sung one after another. Medium and Hard are issue #3724's starting values; only
// Easy is reachable until that issue builds them.
export interface IntervalLevelParams {
  toleranceCents: number;
  holdSeconds: number;
  octaveCounts: boolean; // a note sung an octave off counts as correct
}

export const intervalLevelParams: Record<LearnLevel, IntervalLevelParams> = {
  [LearnLevel.Easy]: { toleranceCents: 50, holdSeconds: 0.6, octaveCounts: true },
  [LearnLevel.Medium]: { toleranceCents: 35, holdSeconds: 0.8, octaveCounts: true },
  [LearnLevel.Hard]: { toleranceCents: 20, holdSeconds: 1.0, octaveCounts: false },
};

export const intervalPatternsPerSession = 10;

// How many sessions on one pattern set tick it off as done, so that Start moves on to the next set
// (interval_training.ts). Michael's call of 2026-10-02 for a start; the plan behind the issue says
// three or four.
export const intervalSessionsPerSet = 2;

// Once a session has introduced its set's patterns, the share of its remaining patterns drawn from
// that set; the rest review the sets before it
export const intervalCurrentSetShare = 0.6;

// Interval Training's own timings, in ms on the audio clock
export const intervalTunables = {
  // A note played on its own on the piano: a rung of the Meet the syllables ladder (issue #3720), a
  // tapped syllable and the house button's do (issue #3721). Long enough to hear the note and say
  // its syllable with it, short enough that tapping down the ladder does not wait on it.
  soloNoteMs: 1000,
  // The gap between the house's do and the pattern it replays on the listen screen: a breath of
  // silence so do stands apart as home rather than sounding like the pattern's first note
  homeGapMs: 400,
  // Each note of the pattern as the listen screen plays it (issue #3721): back to back from the
  // first, with nothing before it, each lit on the staff as it sounds
  patternNoteMs: 800,
  // The guard between a note's outcome and the next note listening. It is not a pause for the
  // singer - the dot has already slid onto the next note's column at the lock, and they move on
  // with it - but the time the known-target estimator needs to be re-aimed at the next note: a
  // frame's estimate is relative to the note the worker was aimed at when it analysed the frame,
  // the frames in flight at a lock (up to six hops of the capture worklet's backlog, about 65 ms at
  // 48 kHz) were analysed against the note just finished, and the new aim reaches the worker only
  // after the page has rendered the outcome and its effect has posted it. Scoring those frames
  // against the next note would read a singer still on the old note as on the new one.
  notePauseMs: 120,
  // After a pattern's last note the screen holds this long, so the final note's colour is seen
  // before the result screen replaces it
  patternEndPauseMs: 500,
  // Scored time on one note with no lock before it ends, as close or missed: the backstop that ends
  // a note nobody resolves, as Pitch Match's noteTimeoutMs is, shorter because a pattern has several
  noteTimeoutMs: 8000,
  // How near a held note must come to count as close (the yellow result): within this of the
  // target for the level's hold time at some point during the note
  closeCents: 100,
  // "Hear it again" on the sing screen: how many times one note can have the pattern replayed
  maxReplays: 2,
  // "Hear it as steps" on the result screen: each note of the filled-in leap, back to back
  stepNoteMs: 500,
};

// ---- Rhythm Echo tunables --------------------------------------------------------------------

export interface RhythmLevelParams {
  bpm: number;
  windowMs: number; // on-time window on either side of the beat
}

export const rhythmLevelParams: Record<LearnLevel, RhythmLevelParams> = {
  [LearnLevel.Easy]: { bpm: 70, windowMs: 120 },
  [LearnLevel.Medium]: { bpm: 90, windowMs: 80 },
  [LearnLevel.Hard]: { bpm: 110, windowMs: 50 },
};

export const rhythmPatternsPerSession = 8;

// ---- Progress record -------------------------------------------------------------------------

export const learnSessionScoreMax = 10; // both modules report a session score out of 10
export const learnUnlockScore = 8; // sessions at or above this count toward unlocking the next level
export const learnUnlockSessions = 3; // qualifying sessions at a level needed to unlock the next one

export const practiceDaysKept = 60; // the hub only needs the current week; keep a little history

export function emptyLearnProgress(updatedAt: Date): LearnProgress {
  return { modules: {}, streakDays: 0, practiceDays: [], updatedAt: updatedAt.toISOString() };
}

export function emptyLearnModuleProgress(): LearnModuleProgress {
  return { level: LearnLevel.Easy, unlocked: [LearnLevel.Easy], bestScore: {}, qualifyingSessions: {}, sessions: 0 };
}

export function moduleProgress(progress: LearnProgress, module: LearnModule): LearnModuleProgress {
  return progress.modules[module] ?? emptyLearnModuleProgress();
}

export function setVocalRange(progress: LearnProgress, range: VocalRange, now: Date): LearnProgress {
  return { ...progress, range, voicePart: voicePartForRange(range), updatedAt: now.toISOString() };
}

// Meet the syllables (issue #3720): the screen that teaches how to say do re mi before a singer's
// first solfège session. It is shown until "Got it, let's start" records it here, once per member.
export function needsSyllables(progress: LearnProgress): boolean {
  return progress.metSyllables !== true;
}

export function markSyllablesMet(progress: LearnProgress, now: Date): LearnProgress {
  return { ...progress, metSyllables: true, updatedAt: now.toISOString() };
}

// ---- Streak and practice days ----------------------------------------------------------------
// Dates are local dates on the device that recorded the session (DateString from date_string.ts).
//
// Streak rules (issue #3470): a session on any day, Sunday included, extends the streak by one; a
// Sunday with no session never breaks it; a missed Monday-to-Saturday day resets it. So practicing
// Sunday after Saturday goes 5 -> 6, and so does practicing Monday after Saturday with Sunday off.

// true when no Monday-to-Saturday day lies strictly between the two dates
function streakSurvives(lastPracticeDate: DateString, today: DateString): boolean {
  for (let date = addDayOffset(lastPracticeDate, 1); dateCompare(date, today) < 0; date = addDayOffset(date, 1)) {
    if (getDayOfWeek(date) !== DayOfWeek.Sunday) {
      return false;
    }
  }
  return true;
}

// Records one completed session on `now`'s local date. Repeat sessions on the same day count as
// sessions (for "this week") but never add to the streak.
export function recordPracticeDay(progress: LearnProgress, now: Date): LearnProgress {
  const today = dateStringFromDate(now);
  const updatedAt = now.toISOString();
  const practiceDays = addPracticeSession(progress.practiceDays, today);
  if (progress.lastPracticeDate === today) {
    return { ...progress, practiceDays, updatedAt };
  }
  const streakDays =
    progress.lastPracticeDate && streakSurvives(progress.lastPracticeDate, today) ? progress.streakDays + 1 : 1;
  return { ...progress, streakDays, lastPracticeDate: today, practiceDays, updatedAt };
}

function addPracticeSession(practiceDays: LearnPracticeDay[], today: DateString): LearnPracticeDay[] {
  const existing = practiceDays.find(({ date }) => date === today);
  const updated = existing
    ? practiceDays.map((day) => (day === existing ? { ...day, sessions: day.sessions + 1 } : day))
    : [{ date: today, sessions: 1 }, ...practiceDays];
  return updated.sort((lhs, rhs) => dateCompare(rhs.date, lhs.date)).slice(0, practiceDaysKept);
}

// The streak to show on the hub: the recorded streak unless a Monday-to-Saturday day has been
// missed since the last practice, in which case it is already 0 (today itself is never "missed").
export function currentStreak(progress: LearnProgress, today: DateString): number {
  if (!progress.lastPracticeDate) {
    return 0;
  }
  return streakSurvives(progress.lastPracticeDate, today) ? progress.streakDays : 0;
}

// The learning streak's value as the Learn tab shows it (issue #3585). The Learn tab's streak is
// each member's own, while the Today tab's streak belongs to the household, so the Learn tab calls
// its own the "learning streak" and gives a live one a phrase of its own rather than a bare count,
// which would read as the same streak. Zero stays a plain count. Defined once so the hub card and
// the session summary agree, and so the phrase can change in one place.
export function learningStreakText(days: number): string {
  if (days === 0) {
    return '0 days';
  }
  return `${days} ${days === 1 ? 'day' : 'days'} in tune`;
}

// Sessions since the most recent Sunday inclusive (on a Sunday, only today), for the hub's "this
// week" card. Counts sessions, not days: three sessions on one day are three sessions this week.
export function sessionsThisWeek(progress: LearnProgress, today: DateString): number {
  const weekStart = mostRecentSunday(today);
  return progress.practiceDays
    .filter(({ date }) => dateCompare(date, weekStart) >= 0 && dateCompare(date, today) <= 0)
    .reduce((total, { sessions }) => total + sessions, 0);
}

// ---- Sessions --------------------------------------------------------------------------------

export interface RecordedSession {
  progress: LearnProgress;
  isNewBest: boolean; // the best score for this level improved (a tie is not a new best)
  unlockedLevel?: LearnLevel; // set when this session unlocked the next level
}

// A pattern set's index when it can be one: a whole number from 0. Anything else - negative,
// fractional - is no set at all, so it never writes a NaN or a key that is not an index into the
// counts the strict schema checks. Which sets a level has is the module's own business
// (interval_training.ts), and this file does not read it: the page only ever passes an index from
// the level's own list.
function patternSetIndex(patternSet: number | undefined): number | undefined {
  if (patternSet === undefined || !Number.isInteger(patternSet) || patternSet < 0) {
    return undefined;
  }
  return patternSet;
}

// One more session on pattern set `patternSet` at `level`, the counts grown with zeros to reach it
function addSetSession(
  setSessions: LearnModuleProgress['setSessions'],
  level: LearnLevel,
  patternSet: number,
): NonNullable<LearnModuleProgress['setSessions']> {
  const counts = [...(setSessions?.[level] ?? [])];
  while (counts.length <= patternSet) {
    counts.push(0);
  }
  counts[patternSet] += 1;
  return { ...setSessions, [level]: counts };
}

// `score` is on the 0..learnSessionScoreMax session scale for any module. `patternSet` is the
// Interval Training pattern set the session played (interval_training.ts), counted for its level in
// setSessions; a session with none - Interval Training's mix of every set, and every other module -
// leaves the counts as they were, and so does one whose `patternSet` could not be a set's index.
//
// The module's record is the one it had with the fields a session changes set over it, so a field
// this function does not know - one a newer bundle added - is carried forward rather than dropped.
//
// `playableLevels` are the levels the module can be played at in this build; absent, every level.
// A newly unlocked level becomes the one to play next only when it is among them: a module whose
// next level is not built yet (Sing in Solfège, Easy only, issue #3722) still records the unlock,
// so the singer has it when the level is built, but keeps the level it can play, so its tile does
// not announce "Medium" over a page that plays Easy.
export function recordLearnSession({
  progress,
  module,
  level,
  score,
  now,
  patternSet,
  playableLevels,
}: {
  progress: LearnProgress;
  module: LearnModule;
  level: LearnLevel;
  score: number;
  now: Date;
  patternSet?: number;
  playableLevels?: readonly LearnLevel[];
}): RecordedSession {
  const current = moduleProgress(progress, module);
  const previousBest = current.bestScore[level];
  const isNewBest = previousBest === undefined || score > previousBest;
  const qualifying = (current.qualifyingSessions[level] ?? 0) + (score >= learnUnlockScore ? 1 : 0);
  const next = nextLearnLevel(level);
  let unlocked = current.unlocked;
  let unlockedLevel: LearnLevel | undefined;
  if (next && !unlocked.includes(next) && qualifying >= learnUnlockSessions) {
    unlocked = [...unlocked, next];
    unlockedLevel = next;
  }
  const setIndex = patternSetIndex(patternSet);
  const updated: LearnModuleProgress = {
    ...current,
    // a newly unlocked level becomes the one to play next, when it can be played
    level: unlockedLevel !== undefined && (playableLevels?.includes(unlockedLevel) ?? true) ? unlockedLevel : level,
    unlocked,
    bestScore: { ...current.bestScore, [level]: isNewBest ? score : previousBest },
    qualifyingSessions: { ...current.qualifyingSessions, [level]: qualifying },
    sessions: current.sessions + 1,
    ...(setIndex !== undefined && { setSessions: addSetSession(current.setSessions, level, setIndex) }),
  };
  const withDay = recordPracticeDay(progress, now);
  return { progress: { ...withDay, modules: { ...withDay.modules, [module]: updated } }, isNewBest, unlockedLevel };
}
